import { compact, flatten, uniq } from 'lodash-es';
import { format } from 'date-fns';
import {
	EventBundleDto,
	EventInstanceDto,
	IntakeFormUserDataResults,
	ListWithTotals,
	OrderListWithTotals,
	TrackingLinkDto,
	UserDto,
	UserToEventBundleDto,
	UserToFollowerDto,
	UserToMembershipDto,
	WaitlistedUserToEventDto,
} from '../models/dto';
import { getUserEmail, getUserName, getUserPhoneNumber } from './user.utils';
import { convertIntakeFormSectionsToGettersMap } from './intake-form.utils';
import { DiscountTypeEnum, UserToEventBundleStatusEnum, UserToMembershipStatusEnum } from '../enums';
import { getCSVContentFromObjects } from './file.utils';
import { priceFormatter } from './price.utils';

// === COMMUNITY FOLLOWERS EXPORT ===

export enum CommunityFollowersHeadersEnum {
	Id = 'ID',
	Name = 'Name',
	Email = 'Email',
	DateJoined = 'Date joined',
	MembershipEnrolment = 'Membership enrolment',
}

export const CommunityFollowersHeadersOrder = [
	CommunityFollowersHeadersEnum.Id,
	CommunityFollowersHeadersEnum.Name,
	CommunityFollowersHeadersEnum.Email,
	CommunityFollowersHeadersEnum.DateJoined,
	CommunityFollowersHeadersEnum.MembershipEnrolment,
];

export const getCommunityFollowersXlsxContent = (
	followers: ListWithTotals<UserToFollowerDto>,
	selectedCommunityProfile: UserDto,
	options: object = {}
): { rows: Record<CommunityFollowersHeadersEnum, string>[]; headers: CommunityFollowersHeadersEnum[] } => {
	const headers = CommunityFollowersHeadersOrder;

	const rows: Record<CommunityFollowersHeadersEnum, string>[] = followers.list.map(({ id, follower, createdAt }) => {
		const membership = follower?.userMemberships?.find(
			({ membership }) => membership?.mainCommunityId === selectedCommunityProfile.id
		)?.membership;

		return {
			[CommunityFollowersHeadersEnum.Id]: id,
			[CommunityFollowersHeadersEnum.Name]: getUserName(follower) ?? '',
			[CommunityFollowersHeadersEnum.Email]: getUserEmail(follower) ?? '',
			[CommunityFollowersHeadersEnum.DateJoined]: createdAt ? format(new Date(createdAt), 'dd MMM, yyyy') : '',
			[CommunityFollowersHeadersEnum.MembershipEnrolment]: membership?.name ?? '',
		} satisfies Record<CommunityFollowersHeadersEnum, string>;
	});

	return { rows, headers };
};

export const getCommunityFollowersFileContent = (
	followers: ListWithTotals<UserToFollowerDto>,
	selectedCommunityProfile: UserDto,
	options: object = {}
): string[][] => {
	const xlsxData = getCommunityFollowersXlsxContent(followers, selectedCommunityProfile, options);
	const data = getCSVContentFromObjects(xlsxData.rows, xlsxData.headers);

	return data;
};

// === TICKETS EXPORT ===

export enum TicketsHeadersEnum {
	TicketId = 'Ticket ID',
	OrderId = 'Order ID',
	OrderPaidAmount = 'Order Paid Amount',
	Purchaser = 'Purchaser',
	PurchaserEmail = 'Purchaser email',
	PurchaserPhone = 'Purchaser phone',
	Tier = 'Tier',
	Coupon = 'Coupon',
	CheckedIn = 'Checked-in',
	CheckedInAt = 'Checked-in At',
	PaidDate = 'Paid Date',
	IsWaiverSigned = 'Waiver Signed',
}

export const TicketsHeadersOrder = [
	TicketsHeadersEnum.TicketId,
	TicketsHeadersEnum.OrderId,
	TicketsHeadersEnum.OrderPaidAmount,
	TicketsHeadersEnum.Purchaser,
	TicketsHeadersEnum.PurchaserEmail,
	TicketsHeadersEnum.Tier,
	TicketsHeadersEnum.Coupon,
	TicketsHeadersEnum.CheckedIn,
	TicketsHeadersEnum.CheckedInAt,
	TicketsHeadersEnum.PaidDate,
	TicketsHeadersEnum.IsWaiverSigned,
];

export type ExportTicketsOptions = {
	hideAmountPaidColumn?: boolean;
	hideCouponColumn?: boolean;
};

export const getTicketsXlsxContent = (
	event: EventInstanceDto,
	orders: OrderListWithTotals,
	options: ExportTicketsOptions = {}
): { rows: Record<TicketsHeadersEnum, string>[]; headers: TicketsHeadersEnum[] } => {
	const intakeFormSections = event.intakeFormSettings?.sections ?? [];
	const intakeFormSectionsToGettersMap = convertIntakeFormSectionsToGettersMap(intakeFormSections);
	const intakeFieldsKeys = Object.keys(intakeFormSectionsToGettersMap);
	const intakeFieldsValues = (intakeFormResults?: IntakeFormUserDataResults) =>
		Object.entries(intakeFormSectionsToGettersMap)
			.map(([key, getter]) => ({ [key]: getter(intakeFormResults) }))
			.reduce((acc, val) => ({ ...acc, ...val }), {});

	let headers = [...TicketsHeadersOrder, ...intakeFieldsKeys.map((key) => key as TicketsHeadersEnum)];

	if (options.hideAmountPaidColumn) {
		headers = headers.filter((header) => header !== TicketsHeadersEnum.OrderPaidAmount);
	}

	if (options.hideCouponColumn) {
		headers = headers.filter((header) => header !== TicketsHeadersEnum.Coupon);
	}

	const rows: Record<TicketsHeadersEnum, string>[] = flatten(
		orders.list.map((order) => {
			const { user, paymentDetails, tickets = [] } = order;
			const amountPaid = paymentDetails ? paymentDetails.amount - paymentDetails.fullFee : 0;

			return tickets.map((ticket, index) => {
				// Show ticket details only once per order
				const showOrderDetails = index < 1;

				return {
					[TicketsHeadersEnum.TicketId]: ticket.shortId,
					[TicketsHeadersEnum.OrderId]: order.id,
					[TicketsHeadersEnum.OrderPaidAmount]: options.hideAmountPaidColumn ? '' : showOrderDetails ? `${amountPaid / 100}` : '',
					[TicketsHeadersEnum.Purchaser]: getUserName(user) ?? '',
					[TicketsHeadersEnum.PurchaserEmail]: getUserEmail(user) ?? '',
					[TicketsHeadersEnum.PurchaserPhone]: getUserPhoneNumber(user) ?? '',
					[TicketsHeadersEnum.Tier]: ticket.eventPrice?.tierName ?? '',
					[TicketsHeadersEnum.Coupon]: options.hideCouponColumn ? '' : ticket.discount?.code ?? '',
					[TicketsHeadersEnum.CheckedIn]: `${ticket.isTicketUsed}`,
					[TicketsHeadersEnum.CheckedInAt]: ticket.usedAt ? new Date(ticket.usedAt).toISOString() : '',
					[TicketsHeadersEnum.PaidDate]: order.paidAt ? new Date(order.paidAt).toISOString() : '',
					[TicketsHeadersEnum.IsWaiverSigned]: `${!!ticket.signedAt}`,
					...intakeFieldsValues(ticket.intakeFormResults),
				} satisfies Record<TicketsHeadersEnum, string>;
			});
		})
	);

	return { rows, headers };
};

export const getEventTicketsFileContent = (
	event: EventInstanceDto,
	orders: OrderListWithTotals,
	options: ExportTicketsOptions = {}
): string[][] => {
	const xlsxData = getTicketsXlsxContent(event, orders, options);
	const data = getCSVContentFromObjects(xlsxData.rows, xlsxData.headers);

	return data;
};

// === ORDERS EXPORT ===

export enum OrdersHeadersEnum {
	OrderId = 'Order ID',
	Purchaser = 'Purchaser',
	PurchaserEmail = 'Purchaser email',
	PurchaserPhone = 'Purchaser phone',
	PriceTiers = 'Tier',
	PaidAmount = 'Order Paid Amount',
	TicketsCount = 'Number of tickets',
	DiscountCodes = 'Coupons',
	PaidDate = 'Paid Date',
}

export const OrdersHeadersOrder = [
	OrdersHeadersEnum.OrderId,
	OrdersHeadersEnum.Purchaser,
	OrdersHeadersEnum.PurchaserEmail,
	OrdersHeadersEnum.PriceTiers,
	OrdersHeadersEnum.PaidAmount,
	OrdersHeadersEnum.TicketsCount,
	OrdersHeadersEnum.DiscountCodes,
	OrdersHeadersEnum.PaidDate,
];

export type ExportOrdersOptions = ExportTicketsOptions & {
	hideTicketsCountColumn?: boolean;
}

export const getOrdersXlsxContent = (
	orders: OrderListWithTotals,
	options: ExportOrdersOptions = {}
): { rows: Record<OrdersHeadersEnum, string>[]; headers: OrdersHeadersEnum[] } => {
	let headers = OrdersHeadersOrder;

	if (options.hideAmountPaidColumn) {
		headers = headers.filter((header) => header !== OrdersHeadersEnum.PaidAmount);
	}

	if (options.hideCouponColumn) {
		headers = headers.filter((header) => header !== OrdersHeadersEnum.DiscountCodes);
	}

	if (options.hideTicketsCountColumn) {
		headers = headers.filter((header) => header !== OrdersHeadersEnum.TicketsCount);
	}

	const rows: Record<OrdersHeadersEnum, string>[] = orders.list.map((order) => {
		const { id, paidAt, user, paymentDetails, tickets = [] } = order;
		const amountPaid = paymentDetails ? paymentDetails.amount - paymentDetails.fullFee : 0;
		const tiers = uniq(compact(tickets.map(({ eventPrice }) => eventPrice?.tierName))).join(', ');
		const coupons = uniq(compact(tickets.map(({ discount }) => discount?.code))).join(', ');

		return {
			[OrdersHeadersEnum.OrderId]: id,
			[OrdersHeadersEnum.Purchaser]: getUserName(user) ?? '',
			[OrdersHeadersEnum.PurchaserEmail]: getUserEmail(user) ?? '',
			[OrdersHeadersEnum.PurchaserPhone]: getUserPhoneNumber(user) ?? '',
			[OrdersHeadersEnum.PriceTiers]: tiers,
			[OrdersHeadersEnum.PaidAmount]: options.hideAmountPaidColumn ? '' : `${amountPaid / 100}`,
			[OrdersHeadersEnum.TicketsCount]: options.hideTicketsCountColumn ? '' : `${paymentDetails?.ticketCount ?? 0}`,
			[OrdersHeadersEnum.DiscountCodes]: options.hideCouponColumn ? '' : coupons,
			[OrdersHeadersEnum.PaidDate]: paidAt ? new Date(paidAt).toISOString() : '',
		} satisfies Record<OrdersHeadersEnum, string>;
	});

	return { rows, headers };
};

export const getOrdersFileContent = (
	orders: OrderListWithTotals,
	options: ExportOrdersOptions = {}
): string[][] => {
	const xlsxData = getOrdersXlsxContent(orders, options);
	const data = getCSVContentFromObjects(xlsxData.rows, xlsxData.headers);

	return data;
};

// === MEMBERSHIPS EXPORT ===

export enum MembersHeadersEnum {
	MemberId = 'Member ID',
	Name = 'Name',
	Email = 'Email',
	Frequency = 'Frequency',
	PaidAmount = 'Paid Amount',
	Coupons = 'Coupons',
	Status = 'Status',
	LastTransactionTime = 'Last Transaction Time',
}

export const MembersHeadersOrder = [
	MembersHeadersEnum.MemberId,
	MembersHeadersEnum.Name,
	MembersHeadersEnum.Email,
	MembersHeadersEnum.Frequency,
	MembersHeadersEnum.PaidAmount,
	MembersHeadersEnum.Coupons,
	MembersHeadersEnum.Status,
	MembersHeadersEnum.LastTransactionTime,
];

export type GetMembersExportFileOptions = {
	t(key: string): string;
};

export const getMembersXlsxContent = (
	members: ListWithTotals<UserToMembershipDto>,
	options: GetMembersExportFileOptions
): { rows: Record<MembersHeadersEnum, string>[]; headers: MembersHeadersEnum[] } => {
	const { t } = options;

	const headers = MembersHeadersOrder;

	const rows: Record<MembersHeadersEnum, string>[] = members.list.map((member) => {
		const { id, user, pauseFuturePayments, active, membershipTier } = member;
		const { createdAt, paymentDetails, discounts = [] } = member.lastOrder ?? {};
		const amountPaid = paymentDetails ? (paymentDetails.amount ?? 0) - (paymentDetails.fullFee ?? 0) : 0;
		const coupons = discounts.map((d) => d.code).join(', ');
		const status = pauseFuturePayments
			? UserToMembershipStatusEnum.Canceled
			: active
			? UserToMembershipStatusEnum.Active
			: UserToMembershipStatusEnum.Finished;

		return {
			[MembersHeadersEnum.MemberId]: id,
			[MembersHeadersEnum.Name]: getUserName(user) ?? '',
			[MembersHeadersEnum.Email]: getUserEmail(user) ?? '',
			[MembersHeadersEnum.Frequency]: membershipTier
				? t(`membership.tiers.frequencies.${membershipTier.frequency}.label`)
				: '',
			[MembersHeadersEnum.PaidAmount]: `${amountPaid / 100}`,
			[MembersHeadersEnum.Coupons]: coupons,
			[MembersHeadersEnum.Status]: t(`membership.subscription.status.${status}`),
			[MembersHeadersEnum.LastTransactionTime]: createdAt ? new Date(createdAt).toISOString() : '',
		} satisfies Record<MembersHeadersEnum, string>;
	});

	return { rows, headers };
};

export const getMembersFileContent = (
	members: ListWithTotals<UserToMembershipDto>,
	options: GetMembersExportFileOptions
): string[][] => {
	const xlsxData = getMembersXlsxContent(members, options);
	const data = getCSVContentFromObjects(xlsxData.rows, xlsxData.headers);

	return data;
};

// === WAITLIST EXPORT ===

export enum WaitlistHeadersEnum {
	WaitlistId = 'Waitlist ID',
	FullName = 'Full Name',
	Email = 'Email',
	Phone = 'Phone',
	JoinedWaitlistAt = 'Joined Waitlist At',
}

export const WaitlistHeadersOrder = [
	WaitlistHeadersEnum.WaitlistId,
	WaitlistHeadersEnum.FullName,
	WaitlistHeadersEnum.Email,
	WaitlistHeadersEnum.Phone,
	WaitlistHeadersEnum.JoinedWaitlistAt,
];


export type GetWaitlistExportFileOptions = object;

export const getEventWaitlistXlsxContent = (
	waitlist: ListWithTotals<WaitlistedUserToEventDto>,
	options: GetWaitlistExportFileOptions
): { rows: Record<WaitlistHeadersEnum, string>[]; headers: WaitlistHeadersEnum[] } => {
	const headers = WaitlistHeadersOrder;

	const rows: Record<WaitlistHeadersEnum, string>[] = waitlist.list.map((waitlistItem) => {
		const { id, user, createdAt } = waitlistItem;

		return {
			[WaitlistHeadersEnum.WaitlistId]: id,
			[WaitlistHeadersEnum.FullName]: getUserName(user) ?? '',
			[WaitlistHeadersEnum.Email]: getUserEmail(user) ?? '',
			[WaitlistHeadersEnum.Phone]: getUserPhoneNumber(user) ?? '',
			[WaitlistHeadersEnum.JoinedWaitlistAt]: createdAt ? new Date(createdAt).toISOString() : '',
		} satisfies Record<WaitlistHeadersEnum, string>;
	});

	return { rows, headers };
};

export const getEventWaitlistFileContent = (
	waitlist: ListWithTotals<WaitlistedUserToEventDto>,
	options: GetWaitlistExportFileOptions
): string[][] => {
	const xlsxData = getEventWaitlistXlsxContent(waitlist, options);
	const data = getCSVContentFromObjects(xlsxData.rows, xlsxData.headers);

	return data;
};

// === EVENT BUNDLES EXPORT ===

export enum EventBundlesMembersHeadersEnum {
	EventBundleId = 'Event Bundle ID',
	MemberId = 'Member ID',
	Name = 'Name',
	Email = 'Email',
	PaidAmount = 'Paid Amount',
	VisitsUsed = 'Visits Used',
	VisitsLeft = 'Visits Left',
	Status = 'Status',
	ActualFrom = 'Actual From',
	ActualTo = 'Actual To',
}

export const EventBundlesMembersHeadersOrder = [
	EventBundlesMembersHeadersEnum.EventBundleId,
	EventBundlesMembersHeadersEnum.MemberId,
	EventBundlesMembersHeadersEnum.Name,
	EventBundlesMembersHeadersEnum.Email,
	EventBundlesMembersHeadersEnum.PaidAmount,
	EventBundlesMembersHeadersEnum.VisitsUsed,
	EventBundlesMembersHeadersEnum.VisitsLeft,
	EventBundlesMembersHeadersEnum.Status,
	EventBundlesMembersHeadersEnum.ActualFrom,
	EventBundlesMembersHeadersEnum.ActualTo,
];

export const getEventBundlesMembersXlsxContent = (
	members: ListWithTotals<UserToEventBundleDto>,
	eventBundle: EventBundleDto,
	options: GetMembersExportFileOptions
): { rows: Record<EventBundlesMembersHeadersEnum, string>[]; headers: EventBundlesMembersHeadersEnum[] } => {
	const { t } = options;

	const headers = EventBundlesMembersHeadersOrder;

	const rows: Record<EventBundlesMembersHeadersEnum, string>[] = members.list.map((member) => {
		const { id, user, actualFrom, actualTo, visitsAmountLeft } = member;
		const { id: eventBundleId, visitsAmount } = eventBundle;
		const { paymentDetails } = member.bundleOrder ?? {};
		const amountPaid = paymentDetails ? (paymentDetails.amount ?? 0) - (paymentDetails.fullFee ?? 0) : 0;
		const status = visitsAmountLeft === 0
			? UserToEventBundleStatusEnum.Finished
			: actualTo < new Date()
			? UserToEventBundleStatusEnum.Expired
			: UserToEventBundleStatusEnum.Active;

		return {
			[EventBundlesMembersHeadersEnum.EventBundleId]: eventBundleId,
			[EventBundlesMembersHeadersEnum.MemberId]: id,
			[EventBundlesMembersHeadersEnum.Name]: getUserName(user) ?? '',
			[EventBundlesMembersHeadersEnum.Email]: getUserEmail(user) ?? '',
			[EventBundlesMembersHeadersEnum.PaidAmount]: `${amountPaid / 100}`,
			[EventBundlesMembersHeadersEnum.VisitsUsed]: `${visitsAmount - visitsAmountLeft}`,
			[EventBundlesMembersHeadersEnum.VisitsLeft]: `${visitsAmountLeft}`,
			[EventBundlesMembersHeadersEnum.Status]: t(`event-bundles.members-statuses.${status}`),
			[EventBundlesMembersHeadersEnum.ActualFrom]: actualFrom ? new Date(actualFrom).toISOString() : '',
			[EventBundlesMembersHeadersEnum.ActualTo]: actualTo ? new Date(actualTo).toISOString() : '',
		} satisfies Record<EventBundlesMembersHeadersEnum, string>;
	});

	return { rows, headers };
};

export const getEventBundlesMembersFileContent = (
	members: ListWithTotals<UserToEventBundleDto>,
	eventBundle: EventBundleDto,
	options: GetMembersExportFileOptions
): string[][] => {
	const xlsxData = getEventBundlesMembersXlsxContent(members, eventBundle, options);
	const data = getCSVContentFromObjects(xlsxData.rows, xlsxData.headers);

	return data;
};

// === TRACKING LINKS EXPORT ===

export enum TrackingLinksHeadersEnum {
	Id = 'ID',
	Deactivated = 'Deactivated',
	Name = 'Name',
	ReferralCode = 'Referral Code',
	DiscountAmount = 'Discount Amount',
	CreatedAt = 'Created At',
	AssignedUserName = 'Assigned User Name',
	AssignedUserPhone = 'Assigned User Phone',
	ViewsCount = 'Views Count',
	TicketsSoldCount = 'Tickets Sold Count',
	ConversionPercent = 'Conversion Percent',
	Amount = 'Amount',
}

export const TrackingLinksHeadersOrder = [
	TrackingLinksHeadersEnum.Id,
	TrackingLinksHeadersEnum.Deactivated,
	TrackingLinksHeadersEnum.Name,
	TrackingLinksHeadersEnum.ReferralCode,
	TrackingLinksHeadersEnum.DiscountAmount,
	TrackingLinksHeadersEnum.CreatedAt,
	TrackingLinksHeadersEnum.AssignedUserName,
	TrackingLinksHeadersEnum.AssignedUserPhone,
	TrackingLinksHeadersEnum.ViewsCount,
	TrackingLinksHeadersEnum.TicketsSoldCount,
	TrackingLinksHeadersEnum.ConversionPercent,
	TrackingLinksHeadersEnum.Amount,
];

export const getTrackingLinksXlsxContent = (
	trackingLinks: TrackingLinkDto[],
	options: GetMembersExportFileOptions
): { rows: Record<TrackingLinksHeadersEnum, string>[]; headers: TrackingLinksHeadersEnum[] } => {
	const { t } = options ?? {};

	const headers = TrackingLinksHeadersOrder;

	const rows: Record<TrackingLinksHeadersEnum, string>[] = trackingLinks.map((link) => {
		const { id, name, referralCode, createdAt, assignedUser, viewsCount, ticketsSoldCount, conversionPercent, earningsAmount, discount } = link;

		return {
			[TrackingLinksHeadersEnum.Id]: id,
			[TrackingLinksHeadersEnum.Deactivated]: `${link.deletedAt ? true : false}`,
			[TrackingLinksHeadersEnum.Name]: name ?? '',
			[TrackingLinksHeadersEnum.ReferralCode]: referralCode ?? '',
			[TrackingLinksHeadersEnum.DiscountAmount]: link.discount
				? link.discount.type === DiscountTypeEnum.Fixed
					? `${priceFormatter(link.discount.amount)}`
					: `${link.discount.amount} %`
				: '',
			[TrackingLinksHeadersEnum.CreatedAt]: createdAt ? new Date(createdAt).toISOString() : '',
			[TrackingLinksHeadersEnum.AssignedUserName]: getUserName(assignedUser) ?? '',
			[TrackingLinksHeadersEnum.AssignedUserPhone]: getUserPhoneNumber(assignedUser) ?? '',
			[TrackingLinksHeadersEnum.ViewsCount]: `${viewsCount ?? 0}`,
			[TrackingLinksHeadersEnum.TicketsSoldCount]: `${ticketsSoldCount ?? 0}`,
			[TrackingLinksHeadersEnum.ConversionPercent]: `${conversionPercent ?? 0}%`,
			[TrackingLinksHeadersEnum.Amount]: `${priceFormatter(earningsAmount ?? 0)}`,
		} satisfies Record<TrackingLinksHeadersEnum, string>;
	});

	return { rows, headers };
};

export const getTrackingLinksFileContent = (
	trackingLinks: TrackingLinkDto[],
	options: GetMembersExportFileOptions
): string[][] => {
	const xlsxData = getTrackingLinksXlsxContent(trackingLinks, options);
	const data = getCSVContentFromObjects(xlsxData.rows, xlsxData.headers);

	return data;
};
